import {LockClosedIcon} from '@heroicons/react/solid';
import {isEmpty} from 'lodash-es';
import React from 'react';

import {
  AmountInput,
  Button,
  CheckBox,
  Input,
  PercentageInput,
  Select,
  SpinningLoader,
} from '@/components';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import Modal from '@/components/Modals';
import type {AfriexAdminAmount, BusinessFee} from '@/types';
import {AfriexCurrencyCode, BusinessFeeAction} from '@/types';
import {logRejection} from '@/utils/logRejection';
import {formatSelectOptionItem} from '@/utils/merchant';

import {FeePredicateInput} from './FeePredicateInput';
import {FeeActionType} from './types';
import {getBusinessIdTargets} from './updateFeeReducer';
import useFeeUpdate from './useFeeUpdate';

interface FeeFormModalProps extends Pick<UserModalProps, 'isOpen' | 'onClose'> {
  initialData?: BusinessFee;
  businessId?: string;
  businessName?: string;
  /** Test hook prefix; each rendered field namespaces under it. */
  formDataCy?: string;
  /** Called after a successful create/update so the list can refresh in place. */
  onSuccess?: () => void | Promise<void>;
}

const FeeFormModal = ({
  isOpen,
  onClose,
  initialData,
  businessId,
  businessName,
  formDataCy,
  onSuccess,
}: FeeFormModalProps) => {
  // When creating a fee from within a merchant profile, pre-seed the scoping
  // tag so the new fee is associated to that enterprise.
  const seededInitialData =
    isEmpty(initialData) && businessId
      ? ({
          tags: [`BUSINESSID_EQUALS_${businessId}`],
          meta: {flags: {isDeactivated: false}},
        } as BusinessFee)
      : initialData;
  const {dispatch, isLoading, handleSubmit, fee} = useFeeUpdate(
    onClose,
    seededInitialData,
    onSuccess,
  );

  const {action, name, amount, tags, meta} = fee;
  // A multi-target fee (BUSINESSID_EQUALS_a,b) also applies to other merchants.
  const sharedWithCount = new Set(
    (tags ?? [])
      .flatMap(getBusinessIdTargets)
      .filter((id) => id !== businessId),
  ).size;
  const flags = meta?.flags ?? {};
  const updateAction = {
    type: FeeActionType.UPDATE_FEE,
    payload: {},
  };
  const updateLabel = isEmpty(initialData) ? 'Create New Fee +' : 'Update Fee';
  const submitLabel = isEmpty(initialData) ? 'Submit' : 'Update';
  const isDisabled =
    isEmpty(fee?.action) ||
    isEmpty(fee?.name) ||
    isLoading ||
    isEmpty(fee?.amount) ||
    isEmpty(fee?.tags);
  const feeActionOptions = Object.values(BusinessFeeAction).map((action) => ({
    label: formatSelectOptionItem(action),
    key: formatSelectOptionItem(action),
    value: action,
  }));
  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-5/12 xl:w-3/12 px-4 sm:px-0"
        closeOnOutsideClick
        title={updateLabel}
        dataCy={formDataCy}
      >
        <div className="pt-3 px-2">
          {isLoading ? (
            <div className="flex w-full h-full justify-center items-center my-5">
              <SpinningLoader />
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                // `handleSubmit` toasts its own failures, so this only fires if
                // that handler itself throws. Log rather than swallow silently.
                handleSubmit(fee).catch(logRejection('Failed to submit fee'));
              }}
              className="flex flex-col"
            >
              {businessId ? (
                <div
                  data-cy={
                    formDataCy ? `${formDataCy}-scope-banner` : undefined
                  }
                  className="mb-3 flex items-center justify-between rounded-lg border border-green-500/60 bg-green-500/10 px-3 py-2"
                >
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Applies to merchant
                    </span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      {businessName || businessId}
                    </span>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                    <LockClosedIcon className="h-3.5 w-3.5" />
                    {sharedWithCount > 0
                      ? `shared with ${sharedWithCount} other ${sharedWithCount === 1 ? 'merchant' : 'merchants'}`
                      : 'scoped'}
                  </span>
                </div>
              ) : null}

              <Input
                label="Name"
                placeholder="Name of Fee"
                dataCy={formDataCy ? `${formDataCy}-name` : undefined}
                value={name || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      name: e.target?.value,
                    },
                  })
                }
                className="w-full mb-2"
              />

              <PercentageInput
                label="Percentage Fee"
                dataCy={formDataCy ? `${formDataCy}-percentage` : undefined}
                value={amount?.valuePercent?.toString() || '0'}
                onValueChange={(rawValue: string) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      amount: {
                        ...amount,
                        valuePercent: Number(
                          rawValue || amount?.valuePercent || 0,
                        ),
                      },
                    },
                  })
                }
              />
              <div className="w-full justify-between flex flex-col md:flex-row gap-1 mt-2">
                <AmountInput
                  label="Fixed Fiat Fee"
                  dataCy={formDataCy ? `${formDataCy}-fixed` : undefined}
                  value={{
                    amount: Number(amount?.valueFixed ?? 0),
                    currency:
                      (amount?.currencyCode as AfriexCurrencyCode) ||
                      AfriexCurrencyCode.USD,
                  }}
                  onChange={(amt: AfriexAdminAmount) =>
                    dispatch({
                      ...updateAction,
                      payload: {
                        amount: {
                          ...amount,
                          valueFixed: amt?.amount?.toString(),
                          currencyCode: amt?.currency,
                        },
                      },
                    })
                  }
                />

                <AmountInput
                  label="Maximum Fee"
                  dataCy={formDataCy ? `${formDataCy}-max` : undefined}
                  fixedCurrency={amount?.currencyCode || AfriexCurrencyCode.USD}
                  value={{
                    amount: Number(amount?.valueMax ?? 0),
                    currency:
                      (amount?.currencyCode as AfriexCurrencyCode) ||
                      AfriexCurrencyCode.USD,
                  }}
                  onChange={(amt: AfriexAdminAmount) =>
                    dispatch({
                      ...updateAction,
                      payload: {
                        amount: {
                          ...amount,
                          valueMax: amt?.amount?.toString(),
                        },
                      },
                    })
                  }
                />
              </div>

              <div className="flex flex-col gap-2 mt-2">
                <Select
                  label="Fee Application Strategy"
                  dataCy={formDataCy ? `${formDataCy}-strategy` : undefined}
                  value={action || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    dispatch({
                      type:
                        action === (e.target?.value as BusinessFeeAction)
                          ? FeeActionType.REMOVE_ACTION_FROM_FEE
                          : FeeActionType.ADD_ACTION_TO_FEE,
                      payload: e.target?.value,
                    })
                  }
                  options={feeActionOptions}
                  placeholder="--Select Strategy--"
                  className="mb-2"
                />
              </div>

              <div className="mb-2">
                <div className="w-full mb-2">
                  <FeePredicateInput
                    tags={tags}
                    businessId={businessId}
                    dataCy={formDataCy ? `${formDataCy}-predicate` : undefined}
                    onRemove={(tag: string) =>
                      dispatch({
                        type: FeeActionType.REMOVE_TAG,
                        payload: tag,
                      })
                    }
                    onChange={(tag: string) =>
                      dispatch({
                        type: FeeActionType.ADD_TAG,
                        payload: tag,
                      })
                    }
                    fieldsToOmit={[]}
                  />
                </div>
              </div>

              <CheckBox
                label="Active"
                type="checkbox"
                dataCy={formDataCy ? `${formDataCy}-active` : undefined}
                checked={Boolean(!flags?.isDeactivated)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      meta: {
                        ...fee.meta,
                        flags: {
                          ...flags,
                          isDeactivated: !e.target?.checked,
                        },
                      },
                    },
                  })
                }
              />

              <div className="text-end pb-5">
                <Button
                  disabled={isDisabled}
                  type="submit"
                  variant="solid"
                  colorScheme="cyan"
                  dataCy={formDataCy ? `${formDataCy}-submit` : undefined}
                >
                  {submitLabel}
                </Button>
              </div>
            </form>
          )}
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default FeeFormModal;
